import { AddTreatmentResponseDto } from "@/data/dto/treatment-source/add-treatment-response-dto";
import { TreatmentSummaryDto } from "@/data/dto/treatment-source/treatment-summary-dto";
import { AddTreatmentRequestPayload } from "@/data/payload/treatment/add-treatment-request-payload";
import { normalizeArrayResponse } from "@/data/source/response-normalizer";
import { URLList } from "@/data/url_list";
import { createLogger, withApiLogging } from "@/utils/logger/logger";
import { FetchApiClientMethod } from "@sadtx/fetch-api-client";
import { errorConverter, SATError, SATResult } from "@sadtx/sat-error";

export type TreatmentListResponseDto = {
  patient?: unknown;
  treatments: TreatmentSummaryDto[];
  canAddTreatment?: boolean | null;
};

export class TreatmentSource {
  private readonly logger = createLogger("TreatmentSource");

  constructor(private readonly client: FetchApiClientMethod) {}

  async fetchTreatmentList(patientUuid: string): Promise<SATResult<TreatmentListResponseDto, SATError>> {
    const url = `${URLList.TREATMENT.LIST}/${patientUuid}`;
    try {
      const data = await withApiLogging(this.logger, { method: "GET", url }, async () => {
        const response = await this.client.get(url);
        return response.data;
      });
      const wrapper = data && typeof data === "object" && !Array.isArray(data)
        ? (data as Record<string, unknown>)
        : {};
      return SATResult.success({
        patient: wrapper.patient,
        treatments: normalizeArrayResponse(data, TREATMENT_LIST_RESPONSE_KEYS) as TreatmentSummaryDto[],
        canAddTreatment:
          typeof wrapper.canAddTreatment === "boolean" ? wrapper.canAddTreatment : null,
      });
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async addTreatment(
    payload: AddTreatmentRequestPayload,
  ): Promise<SATResult<AddTreatmentResponseDto, SATError>> {
    const url = URLList.TREATMENT.ADD_TREATMENT;
    try {
      const data = await withApiLogging(this.logger, { method: "POST", url, payload }, async () => {
        const response = await this.client.post(url, payload);
        return response.data as AddTreatmentResponseDto;
      });
      return SATResult.success(data);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

  async finishTreatment(treatmentUuid: string): Promise<SATResult<void, SATError>> {
    const url = `${URLList.TREATMENT.FINISH}/${treatmentUuid}/finish`;
    try {
      await withApiLogging(this.logger, { method: "PUT", url }, async () => {
        const response = await this.client.put(url);
        return response.data;
      });
      return SATResult.success(void 0);
    } catch (error: unknown) {
      return SATResult.failure(errorConverter.convertError(error));
    }
  }

}

const TREATMENT_LIST_RESPONSE_KEYS = [
  "treatmentList",
  "treatments",
  "dataList",
  "items",
  "list",
  "result",
  "content",
  "data",
] as const;
